import { useState, useCallback } from 'react';

import { createInternalNote } from '../../../../../../api';
import { uploadAttachments, buildMessageWithAttachments } from '../../../utils/uploadAttachments';

const DEFAULT_ERROR_MESSAGE = 'Failed to add note';

type CreateInternalNoteParams = {
  customerId: number;
  text: string;
  attachments: Array<{dataURL?: string; file?: File}>;
};

type UseCreateInternalNoteResult = {
  submit: (params: CreateInternalNoteParams) => Promise<boolean>;
  isSubmitting: boolean;
  error: string | null;
  setError: (error: string | null) => void;
};

export const useCreateInternalNote = (): UseCreateInternalNoteResult => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async({ customerId, text, attachments }: CreateInternalNoteParams): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { paths, urls } = await uploadAttachments(attachments);
      const messageText = buildMessageWithAttachments(text, urls);

      await createInternalNote(customerId, {
        ...(messageText && { text: messageText }),
        ...(paths.length > 0 && { attachments: paths }),
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : DEFAULT_ERROR_MESSAGE;
      setError(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { submit, isSubmitting, error, setError };
};
